import { BUILDING_PERMISSION } from "@shared/legalGuidance";
/**
 * Paketlandningssidor - Data och konfiguration
 * Används av de 6 dedikerade Google Ads-landningssidorna
 * Tekniska specifikationer från PDF-guider (NI_guide_*_v7)
 */
import {
  Sun,
  CloudSun,
  Leaf,
  Snowflake,
  Home,
  type LucideIcon,
} from "lucide-react";
import { IMAGES } from "@/lib/images";

export interface TechSpec {
  label: string;
  value: string;
}

export interface PackageFaq {
  question: string;
  answer: string;
}

export interface PackageLandingData {
  id: string;
  slug: string;
  name: string;
  fullName: string;
  tagline: string;
  heroSubtitle: string;
  season: string;
  months: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  borderColor: string;
  heroImage: string;
  heroImageAlt: string;
  galleryImages: { src: string; alt: string }[];
  glasSystem: string;
  glasTyp: string;
  tak: string;
  stomme: string;
  grund: string;
  golv: string;
  el: string;
  varme: string;
  bygglov: string;
  prisRange: string;
  prisExempel: { storlek: string; pris: string }[];
  techSpecs: TechSpec[];
  varforValjaPunkter: { title: string; desc: string }[];
  description: string;
  detailedDescription: string;
  idealFor: string[];
  faq: PackageFaq[];
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  relatedGuides: { title: string; href: string; description: string }[];
  nextPackage?: { name: string; slug: string; desc: string };
  prevPackage?: { name: string; slug: string; desc: string };
}

export const packageLandingData: Record<string, PackageLandingData> = {
  "sommar-paket": {
    id: "sommar",
    slug: "sommar-paket",
    name: "Sommar",
    fullName: "Sommarpaketet",
    tagline: "Njut av sommaren under tak",
    heroSubtitle:
      "Det enklaste sättet att förlänga din uteplats. Skjutpartier med enkelglas, kanalplasttak och tryckimpregnerad trall ger dig ett skyddat uterum från maj till augusti.",
    season: "Maj - Augusti",
    months: "4 månader",
    icon: Sun,
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    heroImage: IMAGES.pulpet1,
    heroImageAlt: "Sommaruterum med öppna skjutpartier och kanalplasttak",
    galleryImages: [
      { src: IMAGES.pulpet1, alt: "Sommaruterum med pulpettak och enkelglas" },
      { src: IMAGES.pulpet2, alt: "Öppna skjutpartier under sommaren" },
      { src: IMAGES.pulpet5, alt: "Pulpettak-uterum med trall" },
      { src: IMAGES.inglasning1, alt: "Inglasning av altan med skjutpartier" },
      { src: IMAGES.pulpet6, alt: "Sommaruterum sett utifrån" },
      { src: IMAGES.pulpet7, alt: "Ljust sommaruterum med möblering" },
    ],
    glasSystem: "Expodul Solo",
    glasTyp: "Enkelglas 4-5 mm",
    tak: "Kanalplast 16 mm",
    stomme: "Limträ 90x90 mm",
    grund: "Betongplintar + träreglar",
    golv: "Trall, tryckimpregnerad",
    el: "Grundinstallation med LED-belysning",
    varme: "Ej inkluderat",
    bygglov:
      "Ansökningshjälp är inte inkluderad; lovbehov bedöms för fastigheten",
    prisRange: "Från ca 150 000 kr",
    prisExempel: [
      { storlek: "10 kvm", pris: "ca 150 000 kr" },
      { storlek: "15 kvm", pris: "ca 190 000 kr" },
      { storlek: "20 kvm", pris: "ca 230 000 kr" },
    ],
    techSpecs: [
      { label: "Glassystem", value: "Expodul Solo" },
      { label: "Glastyp", value: "Enkelglas 4-5 mm härdat" },
      { label: "Tak", value: "Kanalplast 16 mm UV-skyddad" },
      { label: "Stomme", value: "Limträ 90x90 mm" },
      { label: "Grund", value: "Betongplintar med träreglar" },
      { label: "Golv", value: "Tryckimpregnerad trall" },
      { label: "El", value: "LED-belysning, grundinstallation" },
      { label: "Värme", value: "Ej inkluderat" },
      { label: "Tätning", value: "Grundtätning" },
      { label: "Säsong", value: "Maj - Augusti (4 månader)" },
    ],
    varforValjaPunkter: [
      {
        title: "Lägst investering",
        desc: "Mest uterum för pengarna. Från ca 150 000 kr inklusive montering.",
      },
      {
        title: "Snabb leverans",
        desc: "Enkel konstruktion innebär kort leveranstid. Njut av ditt uterum redan i sommar.",
      },
      {
        title: "Öppna partier",
        desc: "Skjutpartierna öppnas helt och ger dig känslan av att sitta ute, med tak över huvudet.",
      },
      {
        title: "Kontroll av lovbehov",
        desc: "Lovbehovet avgörs av åtgärd, placering och fastighetens förutsättningar.",
      },
      {
        title: "ROT-avdrag",
        desc: "ROT kan medges för berättigad arbetskostnad när Skatteverkets villkor är uppfyllda.",
      },
      {
        title: "Uppgraderingsbar",
        desc: "Börja med sommar och uppgradera till ett mer isolerat paket i framtiden.",
      },
    ],
    description:
      "Det enklaste paketet för dig som vill ha ett tak över huvudet och skydd mot vind och regn under sommarmånaderna. Perfekt som en förlängning av uteplatsen med öppningsbara glaspartier.",
    detailedDescription:
      "Sommarpaketet är det perfekta valet för dig som vill maximera sommarnjutningen utan en stor investering. Med Expodul Solo skjutpartier kan du öppna hela väggen mot trädgården och njuta av frisk luft, samtidigt som kanalplasttaket skyddar mot regn och stark sol. Limträstommen i 90x90 mm ger en stabil konstruktion som tål nordiskt väder, och den tryckimpregnerade trallen skapar ett vackert golv som kräver minimalt underhåll. LED-belysning ingår för mysiga sommarkvällar.",
    idealFor: [
      "Dig som vill förlänga uteplatsen",
      "Familjer som vill äta ute utan att oroa sig för regn",
      "Den som vill ha ett uterum utan stor investering",
      "Husägare som vill testa konceptet innan en större satsning",
    ],
    faq: [
      {
        question: "Behöver jag bygglov för sommarpaketet?",
        answer:
          BUILDING_PERMISSION.summary + " " + BUILDING_PERMISSION.notification,
      },
      {
        question: "Kan jag använda sommarpaketet på vintern?",
        answer:
          "Sommarpaketet är designat för användning maj-augusti. Under kalla månader blir det för kallt inomhus eftersom enkelglaset inte isolerar. Om du vill använda uterummet fler månader rekommenderar vi Förlängd Sommar eller Vår & Höst-paketet.",
      },
      {
        question: "Hur lång tid tar det att bygga?",
        answer:
          "Montering av sommarpaketet tar normalt 3-5 arbetsdagar beroende på storlek och förutsättningar. Från beställning till färdigt uterum räknar vi med 4-8 veckor.",
      },
      {
        question: "Kan jag uppgradera till ett bättre paket senare?",
        answer:
          "Ja, det är möjligt att uppgradera. Grunden och stomme kan i många fall återanvändas. Vi hjälper dig att planera en uppgradering som passar ditt befintliga uterum.",
      },
      {
        question: "Vad kostar sommarpaketet med ROT-avdrag?",
        answer:
          "Se paketets riktpris före eventuellt ROT. Slutpris beror på mått, tillval och platsens förutsättningar. ROT kan endast beräknas på berättigad arbetskostnad när villkoren är uppfyllda.",
      },
    ],
    seoTitle: "Sommaruterum: Njut av sommaren under tak | Från 150 000 kr",
    seoDescription:
      "Sommarpaketet ger dig ett uterum med öppningsbara skjutpartier, kanalplasttak och trall. Från ca 150 000 kr före eventuellt ROT. Ta kontakt.",
    seoKeywords:
      "sommaruterum, uterum sommar, billigt uterum, uterum pris, enkelglas uterum, uterum från 150000",
    relatedGuides: [
      {
        title: "Uterum Pris 2026",
        href: "/uterum/vara-paket/",
        description:
          "Riktpriser, kostnadsdrivare och underlag för jämförbara offerter.",
      },
      {
        title: "Skötselguide för uterum",
        href: "/uterum/fragor/uterum-och-underhall-arlig-skotsel-rengoring-och-vad-som-behover-kontrolleras/",
        description: "Så underhåller du ditt uterum för maximalt värde.",
      },
      {
        title: "Uterum: Komplett guide",
        href: "/uterum/",
        description: "Allt du behöver veta om uterum, typer och konstruktion.",
      },
    ],
    nextPackage: {
      name: "Förlängd Sommar",
      slug: "forlangd-sommar-paket",
      desc: "Förläng säsongen till april-oktober med lågemissionsglas.",
    },
  },

  "forlangd-sommar-paket": {
    id: "forlangd-sommar",
    slug: "forlangd-sommar-paket",
    name: "Förlängd Sommar",
    fullName: "Förlängd Sommar-paketet",
    tagline: "Förläng säsongen med månader",
    heroSubtitle:
      "Lågemissionsglas och tjockare kanalplast ger dig ett uterum du kan använda från april till oktober. Borsttätningar och kullagrade hjul för smidig funktion.",
    season: "April - Oktober",
    months: "7 månader",
    icon: CloudSun,
    color: "text-orange-600",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
    heroImage: IMAGES.pulpet3,
    heroImageAlt: "Förlängd sommar-uterum med lågemissionsglas och kanalplast",
    galleryImages: [
      { src: IMAGES.pulpet3, alt: "Uterum med lågemissionsglas och pulpettak" },
      { src: IMAGES.pulpet4, alt: "Förlängd sommar-uterum med möblering" },
      { src: IMAGES.pulpet8, alt: "Skjutpartier med borsttätningar" },
      { src: IMAGES.inglasning2, alt: "Inglasning med förbättrad isolering" },
      { src: IMAGES.pulpet9, alt: "Uterum i höstljus" },
      { src: IMAGES.pulpet10, alt: "Detaljbild av skjutpartier" },
    ],
    glasSystem: "Nordic Låg-E skjutpartier",
    glasTyp: "Härdat Låg-E energiglas 4 mm (U-värde ca 3,6 W/m²K)",
    tak: "Kanalplast 32 mm UV-skyddad",
    stomme: "Limträ 115x115 mm",
    grund: "Betongplintar + träreglar",
    golv: "Trall, tryckimpregnerad",
    el: "Grundinstallation med LED-belysning",
    varme: "Ej inkluderat (tillval)",
    bygglov:
      "Ansökningshjälp är inte inkluderad; lovbehov bedöms för fastigheten",
    prisRange: "Från ca 200 000 kr",
    prisExempel: [
      { storlek: "10 kvm", pris: "ca 200 000 kr" },
      { storlek: "15 kvm", pris: "ca 260 000 kr" },
      { storlek: "20 kvm", pris: "ca 320 000 kr" },
    ],
    techSpecs: [
      { label: "Glassystem", value: "Expodul Twin Light" },
      { label: "Glastyp", value: "Lågemissionsglas (Låg-E)" },
      { label: "Tak", value: "Kanalplast 32 mm UV-skyddad" },
      { label: "Stomme", value: "Limträ 115x115 mm" },
      { label: "Grund", value: "Betongplintar med träreglar" },
      { label: "Golv", value: "Tryckimpregnerad trall" },
      { label: "Tätning", value: "Borsttätningar, kullagrade hjul" },
      { label: "El", value: "LED-belysning, grundinstallation" },
      { label: "Värme", value: "Tillval" },
      { label: "Säsong", value: "April - Oktober (7 månader)" },
    ],
    varforValjaPunkter: [
      {
        title: "7 månaders säsong",
        desc: "Använd uterummet från tidig vår till sen höst. Tre extra månader jämfört med sommarpaketet.",
      },
      {
        title: "Lågemissionsglas",
        desc: "Reflekterar tillbaka värmen och håller uterummet varmare under svalare dagar.",
      },
      {
        title: "Borsttätningar",
        desc: "Förbättrad vindtäthet gör att du slipper drag och kan njuta av uterummet även blåsiga dagar.",
      },
      {
        title: "Kraftigare stomme",
        desc: "Limträ 115x115 mm ger en stabilare konstruktion som tål mer belastning.",
      },
      {
        title: "UV-skyddad kanalplast",
        desc: "32 mm tjock kanalplast med UV-skydd ger bättre isolering och längre livslängd.",
      },
      {
        title: "Bra pris-prestanda",
        desc: "Betydligt bättre komfort än sommarpaketet till en rimlig merkostnad.",
      },
    ],
    description:
      "Steget upp från sommarpaketet. Lågemissionsglaset och den tjockare kanalplasten gör att du kan använda uterummet från tidig vår till sen höst.",
    detailedDescription:
      "Förlängd Sommar-paketet är det smartaste valet för dig som vill ha mer än bara sommar. Med Expodul Twin Light och lågemissionsglas (Låg-E) reflekteras värmen tillbaka in i uterummet, vilket gör att temperaturen hålls behaglig även under svalare vår- och höstdagar. Den 32 mm tjocka kanalplasten med UV-skydd ger nästan dubbelt så bra isolering jämfört med sommarpaketet, och borsttätningarna med kullagrade hjul gör att partierna glider smidigt och tätar bättre mot vind och drag. Den kraftigare limträstommen i 115x115 mm ger en stabil och hållbar konstruktion.",
    idealFor: [
      "Dig som vill använda uterummet från april till oktober",
      "Familjer som vill äta frukost ute redan på våren",
      "Den som vill ha bättre isolering utan att gå till fullisolerat",
      "Husägare som prioriterar bra pris-prestanda",
    ],
    faq: [
      {
        question: "Vad är skillnaden mot sommarpaketet?",
        answer:
          "Förlängd Sommar har lågemissionsglas istället för enkelglas, 32 mm kanalplast istället för 16 mm, kraftigare limträstomme (115x115 mm vs 90x90 mm) och borsttätningar med kullagrade hjul. Det ger 7 månaders användning istället för 4.",
      },
      {
        question: "Kan jag lägga till golvvärme?",
        answer:
          "Golvvärme finns som tillval. Elektrisk golvvärme under trallen kan förstärka komforten under svalare dagar, men för helårsbruk rekommenderar vi Vår & Höst eller Vinterbonat-paketet.",
      },
      {
        question: "Behöver jag bygglov?",
        answer:
          BUILDING_PERMISSION.summary + " " + BUILDING_PERMISSION.notification,
      },
      {
        question: "Hur fungerar lågemissionsglas?",
        answer:
          "Lågemissionsglas (Låg-E) har en tunn metalloxidbeläggning som reflekterar tillbaka långvågig värmestrålning. Det innebär att värmen stannar kvar i uterummet istället för att stråla ut genom glaset, vilket ger märkbart bättre komfort under svalare dagar.",
      },
      {
        question: "Vad kostar Förlängd Sommar med ROT-avdrag?",
        answer:
          "Se paketets riktpris före eventuellt ROT. Slutpris beror på mått, tillval och platsens förutsättningar. ROT kan endast beräknas på berättigad arbetskostnad när villkoren är uppfyllda.",
      },
    ],
    seoTitle: "Förlängd Sommar-uterum: April till Oktober | Från 200 000 kr",
    seoDescription:
      "Förlängd Sommar-paketet med lågemissionsglas och 32 mm kanalplast ger dig 7 månaders uterumssäsong. Från ca 200 000 kr före eventuellt ROT. Ta kontakt.",
    seoKeywords:
      "förlängd sommar uterum, uterum vår höst, lågemissionsglas uterum, uterum pris 200000, uterum 7 månader",
    relatedGuides: [
      {
        title: "Uterum Pris 2026",
        href: "/uterum/vara-paket/",
        description:
          "Riktpriser, kostnadsdrivare och underlag för jämförbara offerter.",
      },
      {
        title: "Kondens i uterum",
        href: "/uterum/kondens/",
        description: "Så undviker du kondens och fuktproblem i ditt uterum.",
      },
      {
        title: "Glaspartier för uterum",
        href: "/uterum/glaspartier/",
        description: "Jämför olika glastyper och system för uterum.",
      },
    ],
    prevPackage: {
      name: "Sommar",
      slug: "sommar-paket",
      desc: "Enklare uterum för sommarmånaderna.",
    },
    nextPackage: {
      name: "Vår & Höst",
      slug: "var-host-paket",
      desc: "Isolerpartier med lyft-skjut för 9 månaders komfort.",
    },
  },

  "var-host-paket": {
    id: "var-host",
    slug: "var-host-paket",
    name: "Vår & Höst",
    fullName: "Vår & Höst-paketet",
    tagline: "Tre årstider av komfort",
    heroSubtitle:
      "Vårt mest populära paket. 2-glas isolerpartier med lyft-skjut-funktion, isolerad grund och golv ger dig ett uterum du kan njuta av från mars till november.",
    season: "Mars - November",
    months: "9 månader",
    icon: Leaf,
    color: "text-emerald-600",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-200",
    heroImage: IMAGES.villa2,
    heroImageAlt: "Vår & Höst-uterum med 2-glas isolerpartier och lyft-skjut",
    galleryImages: [
      { src: IMAGES.villa2, alt: "Villa med Vår & Höst-uterum" },
      { src: IMAGES.villa1, alt: "Uterum med isolerpartier på klassisk villa" },
      { src: IMAGES.cornerDaytime, alt: "Hörn-uterum med dagsljus" },
      { src: IMAGES.uterumDayFurnished, alt: "Möblerat uterum i dagsljus" },
      { src: IMAGES.houseWithModule, alt: "Villa med uterumsmodul" },
      { src: IMAGES.villa3, alt: "Uterum med isolerad grund" },
    ],
    glasSystem: "Schüco ASS 32, 2-glas isolerkassetter",
    glasTyp: "2-glas isolerglas (U-värde ca 1,3 W/m²K), ej bruten köldbrygga",
    tak: "Kanalplast, glastak eller plåttak",
    stomme: "Förstärkt limträ eller bärande konstruktion",
    grund: "Isolerad plintgrund eller platta på mark",
    golv: "Isolerat golv med valfri beläggning",
    el: "Komplett elinstallation",
    varme: "Golvvärme som tillval",
    bygglov: "Ingår",
    prisRange: "Från ca 300 000 kr",
    prisExempel: [
      { storlek: "12 kvm", pris: "ca 300 000 kr" },
      { storlek: "18 kvm", pris: "ca 400 000 kr" },
      { storlek: "25 kvm", pris: "ca 500 000 kr" },
    ],
    techSpecs: [
      { label: "Glassystem", value: "Nordic 2-glas isolerpartier" },
      { label: "Glastyp", value: "2-glas isolerglas" },
      { label: "Funktion", value: "Lyft-skjut (smooth-glide)" },
      { label: "Tak", value: "Kanalplast / glastak / plåttak" },
      { label: "Stomme", value: "Förstärkt limträ / bärande konstruktion" },
      { label: "Grund", value: "Isolerad plintgrund eller platta" },
      { label: "Golv", value: "Isolerat golv, valfri beläggning" },
      { label: "El", value: "Komplett elinstallation" },
      { label: "Värme", value: "Golvvärme som tillval" },
      { label: "Bygglov", value: "Ingår i paketet" },
    ],
    varforValjaPunkter: [
      {
        title: "9 månaders säsong",
        desc: "Från tidig vår till sen höst. Med golvvärme kan du förlänga ytterligare.",
      },
      {
        title: "Mest populära paketet",
        desc: "Vår bästsäljare. Perfekt balans mellan komfort, kvalitet och investering.",
      },
      {
        title: "Lyft-skjut-partier",
        desc: "Smidiga isolerpartier som lyfts och glider tyst. Enkel hantering med en hand.",
      },
      {
        title: "Isolerad grund och golv",
        desc: "Isolerad plintgrund eller platta ger ett varmt golv utan drag.",
      },
      {
        title: "Hjälp med bygglovsunderlag ingår enligt paketets omfattning",
        desc: "Vi tar hand om hela bygglovsprocessen: ritningar, ansökan och dialog med kommunen.",
      },
      {
        title: "Valfritt takmaterial",
        desc: "Välj mellan kanalplast, glastak eller plåttak beroende på stil och budget.",
      },
    ],
    description:
      "Det mest populära paketet. Isolerande glaspartier med lyft-skjut-funktion ger en bekväm temperatur från tidig vår till sen höst.",
    detailedDescription:
      "Vår & Höst-paketet är Nordiska Inglasningars bästsäljare och det finns en god anledning till det. Med Nordic 2-glas isolerpartier och lyft-skjut-funktion får du ett uterum som håller en behaglig temperatur från mars till november. Den isolerade grunden och golvet eliminerar drag underifrån, och med golvvärme som tillval kan du förlänga säsongen ytterligare. Hjälp med bygglovsunderlag ingår enligt paketets omfattning i paketet, och vi tar hand om hela processen från ritningar till godkännande. Du kan välja mellan kanalplast, glastak eller plåttak beroende på dina preferenser och husets arkitektur.",
    idealFor: [
      "Dig som vill använda uterummet 9 månader om året",
      "Familjer som vill ha ett extra vardagsrum under vår och höst",
      "Den som söker bästa balansen mellan pris och komfort",
      "Husägare som vill ha ett komplett paket med bygglov",
    ],
    faq: [
      {
        question: "Varför är Vår & Höst det mest populära paketet?",
        answer:
          "Det erbjuder den bästa balansen mellan komfort och investering. 2-glas isolerpartier med lyft-skjut ger 9 månaders användning, och med golvvärme som tillval kan du förlänga ytterligare. Hjälp med bygglovsunderlag ingår enligt paketets omfattning, och priset är rimligt jämfört med helårslösningar.",
      },
      {
        question: "Kan jag använda det på vintern?",
        answer:
          "Under mildare vinterdagar med golvvärme kan du använda uterummet, men det är inte designat för helårsbruk. För att använda uterummet hela vintern rekommenderar vi Vinterbonat eller Boyta-paketet.",
      },
      {
        question: "Vad är lyft-skjut-funktion?",
        answer:
          "Lyft-skjut innebär att glaspartiet lyfts upp från sin tätning och sedan glider i sidled på en räls. Det ger en mycket tät konstruktion när partiet är stängt och en smidig öppning med en hand.",
      },
      {
        question: "Vilka takmaterial kan jag välja?",
        answer:
          "Du kan välja mellan kanalplast (mest kostnadseffektivt), glastak (mest ljusinsläpp) eller plåttak (mest hållbart och bäst isolering). Vi hjälper dig att välja rätt baserat på ditt hus och dina önskemål.",
      },
      {
        question: "Vad kostar Vår & Höst med ROT-avdrag?",
        answer:
          "Se paketets riktpris före eventuellt ROT. Slutpris beror på mått, tillval och platsens förutsättningar. ROT kan endast beräknas på berättigad arbetskostnad när villkoren är uppfyllda.",
      },
    ],
    seoTitle: "Vår & Höst-uterum: 9 månaders komfort | Från 300 000 kr",
    seoDescription:
      "Vår & Höst-paketet med 2-glas isolerpartier och lyft-skjut ger dig 9 månaders uterumssäsong. Hjälp med bygglovsunderlag ingår enligt paketets omfattning. Från ca 300 000 kr. Ta kontakt.",
    seoKeywords:
      "vår höst uterum, uterum isolerpartier, lyft-skjut uterum, uterum 300000, uterum 9 månader, populärt uterum",
    relatedGuides: [
      {
        title: "Uterum Pris 2026",
        href: "/uterum/vara-paket/",
        description:
          "Riktpriser, kostnadsdrivare och underlag för jämförbara offerter.",
      },
      {
        title: "Glaspartier för uterum",
        href: "/uterum/glaspartier/",
        description: "Jämför olika glastyper och system för uterum.",
      },
      {
        title: "Bygglov för uterum",
        href: "/uterum/bygglov/",
        description: "Allt om bygglov, regler och handläggningstider.",
      },
    ],
    prevPackage: {
      name: "Förlängd Sommar",
      slug: "forlangd-sommar-paket",
      desc: "Lågemissionsglas för april-oktober.",
    },
    nextPackage: {
      name: "Vinterbonat",
      slug: "vinterbonat-paket",
      desc: "Schüco ASE 60 för helårsbruk.",
    },
  },

  "vinterbonat-paket": {
    id: "vinterbonat",
    slug: "vinterbonat-paket",
    name: "Vinterbonat",
    fullName: "Vinterbonat-paketet",
    tagline: "Helårsbruk med komfort",
    heroSubtitle:
      "Schüco ASE 60 med termiskt brutna profiler och 2-glas isolerglas. Isolerat tak, golv och grund med golvvärme ger dig ett uterum du kan använda hela året.",
    season: "Januari - December",
    months: "12 månader",
    icon: Snowflake,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    heroImage: IMAGES.villa4,
    heroImageAlt:
      "Vinterbonat uterum med Schüco ASE 60 och isolerad konstruktion",
    galleryImages: [
      { src: IMAGES.villa4, alt: "Vinterbonat uterum med Schüco-profiler" },
      {
        src: IMAGES.cornerEvening,
        alt: "Hörn-uterum med kvällsbelysning vintertid",
      },
      { src: IMAGES.uterumNightFurnished, alt: "Möblerat uterum kvällstid" },
      {
        src: IMAGES.houseArmchairs,
        alt: "Uterum med fåtöljer och mysig atmosfär",
      },
      { src: IMAGES.vyUtanSno, alt: "Vinterbonat uterum utan snö" },
      {
        src: IMAGES.houseCozyCashmere,
        alt: "Mysig inredning i vinterbonat uterum",
      },
    ],
    glasSystem: "Schüco ASS 32, brutna köldbryggor",
    glasTyp:
      "2-glas energiglas 24 mm, härdat + varmkant (U-värde ca 1,1 W/m²K)",
    tak: "Isolerat uterumstak",
    stomme: "Förstärkt limträ eller bärande konstruktion",
    grund: "Isolerad platta eller plintgrund",
    golv: "Isolerat golv med golvvärme",
    el: "Komplett elinstallation",
    varme: "Golvvärme (el/vatten)",
    bygglov: "Ingår",
    prisRange: "Från ca 400 000 kr",
    prisExempel: [
      { storlek: "12 kvm", pris: "ca 400 000 kr" },
      { storlek: "18 kvm", pris: "ca 520 000 kr" },
      { storlek: "25 kvm", pris: "ca 650 000 kr" },
    ],
    techSpecs: [
      { label: "Glassystem", value: "Schüco ASE 60" },
      { label: "Glastyp", value: "2-glas isolerglas" },
      { label: "Profiler", value: "Termiskt bruten aluminium" },
      { label: "Tak", value: "Isolerat uterumstak" },
      { label: "Stomme", value: "Förstärkt limträ / bärande konstruktion" },
      { label: "Grund", value: "Isolerad platta eller plintgrund" },
      { label: "Golv", value: "Isolerat golv med golvvärme" },
      { label: "El", value: "Komplett elinstallation" },
      { label: "Värme", value: "Golvvärme (el/vatten)" },
      { label: "Bygglov", value: "Ingår i paketet" },
    ],
    varforValjaPunkter: [
      {
        title: "Helårsbruk",
        desc: "Använd uterummet 365 dagar om året. Schüco ASE 60 och isolering håller värmen.",
      },
      {
        title: "Schüco ASE 60",
        desc: "Världsledande profiler med termiskt bruten aluminium. Eliminerar köldbryggor.",
      },
      {
        title: "Golvvärme ingår",
        desc: "Välj mellan elektrisk eller vattenburen golvvärme för jämn och behaglig värme.",
      },
      {
        title: "Isolerat klimatskal",
        desc: "Tak, golv och grund är isolerade för att minimera värmeförluster.",
      },
      {
        title: "Hjälp med bygglovsunderlag ingår enligt paketets omfattning",
        desc: "Komplett bygglovshantering med ritningar, ansökan och dialog med kommunen.",
      },
      {
        title: "Steget före Boyta",
        desc: "Jämför Vinterbonat med Bostandard och Boyta utifrån användning, isolering och projektering.",
      },
    ],
    description:
      "Schüco ASE 60-systemet med termiskt brutna profiler och 2-glas isolerglas ger ett uterum du kan använda hela året.",
    detailedDescription:
      "Vinterbonat-paketet är för dig som vill ha ett uterum som fungerar hela året utan kompromisser. Med Schüco ASE 60 lyft-skjutsystem och termiskt brutna aluminiumprofiler elimineras köldbryggor, och 2-glas isolerglas håller värmen inne. Det isolerade taket, golvet och grunden skapar ett komplett klimatskal som gör att uterummet håller en behaglig temperatur även under kalla vintermånader. Golvvärme ingår i paketet, och du kan välja mellan elektrisk eller vattenburen variant. Hjälp med bygglovsunderlag ingår enligt paketets omfattning, och vi tar hand om hela processen.",
    idealFor: [
      "Dig som vill använda uterummet hela året",
      "Familjer som vill ha ett extra rum med projektspecifik kravbedömning",
      "Den som prioriterar komfort och kvalitet",
      "Husägare som vill ha Schüco-kvalitet till rimligt pris",
    ],
    faq: [
      {
        question: "Vad är skillnaden mellan Vinterbonat och Boyta?",
        answer:
          "Vinterbonat har Schüco ASE 60 med 2-glas. Boyta har Schüco ASE 80.HI med 3-glas och ett annat klimatskal. Avsedd användning, tillämpliga byggkrav och klassificering av boarea måste bedömas för det färdiga projektet.",
      },
      {
        question: "Hur varmt blir det på vintern?",
        answer:
          "Med golvvärme och isolerat klimatskal håller Vinterbonat-uterummet normalt 18-22 grader även under kalla vinterdagar. Exakt temperatur beror på storlek, orientering och golvvärmens effekt.",
      },
      {
        question: "Vad är termiskt brutna profiler?",
        answer:
          "Termiskt brutna profiler har en isolerande plastbrygga mellan den inre och yttre aluminiumprofilen. Det eliminerar köldbryggor och förhindrar att kyla leds in genom profilen, vilket ger betydligt bättre värmeisolering.",
      },
      {
        question: "Ingår golvvärme i priset?",
        answer:
          "Ja, golvvärme ingår i Vinterbonat-paketet. Du kan välja mellan elektrisk golvvärme (enklare installation) eller vattenburen golvvärme (kopplad till husets värmesystem, mer energieffektiv).",
      },
      {
        question: "Vad kostar Vinterbonat med ROT-avdrag?",
        answer:
          "Se paketets riktpris före eventuellt ROT. Slutpris beror på mått, tillval och platsens förutsättningar. ROT kan endast beräknas på berättigad arbetskostnad när villkoren är uppfyllda.",
      },
    ],
    seoTitle: "Vinterbonat uterum: Helårsbruk med Schüco | Från 400 000 kr",
    seoDescription:
      "Vinterbonat-paketet med Schüco ASE 60, termiskt brutna profiler och golvvärme. Använd ditt uterum hela året. Från ca 400 000 kr. Ta kontakt.",
    seoKeywords:
      "vinterbonat uterum, uterum helår, Schüco uterum, isolerat uterum, uterum golvvärme, uterum 400000",
    relatedGuides: [
      {
        title: "Vinterbonat uterum: Komplett guide",
        href: "/uterum/vinterbonat/",
        description: "Allt om isolering, uppvärmning och helårslösningar.",
      },
      {
        title: "Uppvärmning av uterum",
        href: "/uterum/uppvarmning/",
        description: "Golvvärme, värmepumpar och andra värmekällor.",
      },
      {
        title: "Vår fabrik i Eskilstuna",
        href: "/uterum/var-fabrik/",
        description: "Så tillverkas ditt uterum i vår egen fabrik.",
      },
    ],
    prevPackage: {
      name: "Vår & Höst",
      slug: "var-host-paket",
      desc: "2-glas isolerpartier för mars-november.",
    },
    nextPackage: {
      name: "Boyta",
      slug: "boyta-paket",
      desc: "Schüco ASE 80.HI och 3-glas för projekt som planeras för bostadsbruk.",
    },
  },

  "bostandard-paket": {
    id: "bostandard",
    slug: "bostandard-paket",
    name: "Bostandard",
    fullName: "Bostandard-paketet",
    tagline: "Vinterbonad standard – helårsbruk",
    heroSubtitle:
      "Schüco ASE 60 med termiskt brutna aluminiumprofiler och 2-glas isolerglas 27 mm. Isolerat uterumstak med kanalplast 50 mm, vår tjockaste. En fullt isolerad vinterträdgårdslösning för dig som vill använda rummet hela året.",
    season: "Januari - December",
    months: "12 månader",
    icon: Snowflake,
    color: "text-indigo-600",
    bgColor: "bg-indigo-50",
    borderColor: "border-indigo-200",
    heroImage: IMAGES.villa4,
    heroImageAlt:
      "Bostandard uterum med Schüco ASE 60 och isolerad konstruktion",
    galleryImages: [
      { src: IMAGES.villa4, alt: "Bostandard uterum med Schüco ASE 60" },
      { src: IMAGES.cornerEvening, alt: "Hörn-uterum med kvällsbelysning" },
      { src: IMAGES.uterumNightFurnished, alt: "Möblerat uterum kvällstid" },
      { src: IMAGES.houseArmchairs, alt: "Uterum med fåtöljer" },
      { src: IMAGES.vyUtanSno, alt: "Bostandard uterum utan snö" },
      {
        src: IMAGES.houseCozyCashmere,
        alt: "Mysig inredning i bostandard uterum",
      },
    ],
    glasSystem: "Schüco ASE 60, termiskt brutna profiler",
    glasTyp:
      "2-glas isolerglas 27 mm, argon + energibeläggning + varmkant (U-värde ca 1,1 W/m²K)",
    tak: "Isolerat uterumstak, kanalplast 50 mm (vår tjockaste)",
    stomme: "Förstärkt limträ eller annan bärande konstruktion",
    grund: "Isolerad platta på mark eller plintgrund med bjälklag",
    golv: "Isolerad konstruktion, golvbeläggning som tillval",
    el: "Elinstallation som tillval (via behörig elektriker)",
    varme: "Golvvärme som tillval (el eller vatten)",
    bygglov: "Ingår i uppdraget",
    prisRange: "Från ca 450 000 kr",
    prisExempel: [
      { storlek: "12 kvm", pris: "ca 450 000 kr" },
      { storlek: "18 kvm", pris: "ca 580 000 kr" },
      { storlek: "25 kvm", pris: "ca 720 000 kr" },
    ],
    techSpecs: [
      { label: "Glassystem", value: "Schüco ASE 60" },
      { label: "Glastyp", value: "2-glas isolerglas 27 mm, argon + varmkant" },
      { label: "U-värde glas", value: "Ca 1,1 W/m²K" },
      { label: "Köldbrygga", value: "Bruten i profilerna" },
      { label: "Tak", value: "Kanalplast 50 mm, UV-skyddad (vår tjockaste)" },
      { label: "Stomme", value: "Förstärkt limträ, dimensionerad" },
      {
        label: "Grund",
        value: "Isolerad platta eller plintgrund med bjälklag",
      },
      { label: "Golv", value: "Isolerad konstruktion, beläggning som tillval" },
      { label: "Tätning", value: "Drevning, systempackningar och tätlister" },
      { label: "Bygglov", value: "Ingår i uppdraget" },
    ],
    varforValjaPunkter: [
      {
        title: "Helårsbruk med vinterbonad standard",
        desc: "Schüco ASE 60 med termiskt brutna profiler och isolerglas ger ett rum med tydligt bättre täthet och komfort än ett säsongsuterum.",
      },
      {
        title: "Vår tjockaste kanalplast",
        desc: "Isolerat uterumstak med 50 mm kanalplast som standard. Glastak eller plåt- och sandwichtak som uppgradering.",
      },
      {
        title: "Anpassad grundlösning",
        desc: "Grund och golv projekteras utifrån din tomts förutsättningar – isolerad platta eller plintgrund med bjälklag.",
      },
      {
        title: "Komplett helhetslösning",
        desc: "Vi tar hand om hela processen – från planering till färdig vinterträdgård. Grund, stomme, tak, glaspartier och färdigställande.",
      },
      {
        title: "Hjälp med bygglovsunderlag ingår enligt paketets omfattning",
        desc: "Vi bistår med bygglovsansökan samt erforderliga ritningar och handlingar för projektet.",
      },
      {
        title: "Uppgraderingsbar",
        desc: "Kan uppgraderas till Boyta-paketet med Schüco ASE 80.HI och 3-glas för fullvärdig bostadsyta.",
      },
    ],
    description:
      "Bostandard-paketet är för dig som vill njuta av trädgården och naturen under hela året. Med Schüco ASE 60, termiskt brutna aluminiumprofiler och 2-glas isolerglas får du ett rum med tydligt bättre täthet och komfort än ett traditionellt säsongsuterum.",
    detailedDescription:
      "Resultatet är en varm och inbjudande vinterträdgård som förlänger ditt hem ut mot naturen. Grund och golv projekteras utifrån din tomts förutsättningar, marknivåer och anslutande höjder. Vi väljer den lösning som passar bäst – isolerad platta på mark eller plintgrund med bjälklag. Golvvärme kan läggas till som tillval för ökad komfort under kalla delar av året. Taket utförs som isolerat uterumstak med 50 mm kanalplast som standard, vår tjockaste kanalplast. Vill du ha ett annat uttryck väljs isolerande glastak eller plåt- och sandwichtak som uppgradering.",
    idealFor: [
      "Dig som vill använda uterummet hela året med kompletterande uppvärmning",
      "Familjer som vill ha en vinterträdgård med hög komfort",
      "Villaägare som vill förlänga hemmet ut mot naturen",
      "Dig som vill ha bättre isolering än Vinterbonat-paketet men vill jämföra olika isoleringsnivåer",
    ],
    faq: [
      {
        question: "Vad skiljer Bostandard från Vinterbonat-paketet?",
        answer:
          "Både Bostandard och Vinterbonat anges med Schüco ASE 60 i paketmatrisen. Bostandard har 27 mm isolerglas och 50 mm kanalplasttak. Jämför hela specifikationen, grund, värme och tillval i en offert; paketnamnet ensamt beskriver inte alla skillnader.",
      },
      {
        question: "Behöver jag kompletterande uppvärmning?",
        answer:
          "Ja, under de kallaste perioderna behövs kompletterande uppvärmning. Golvvärme (el eller vatten) är det vanligaste tillvalet. Infravärmare är ett annat alternativ.",
      },
      {
        question: "Ingår bygglov i Bostandard-paketet?",
        answer:
          "Ja, bygglovsansökan och projektering ingår i uppdraget. Vi tar fram erforderliga ritningar och handlingar.",
      },
      {
        question: "Kan jag uppgradera till Boyta-paketet senare?",
        answer:
          "Ja, men det är enklare och billigare att välja rätt paket från start. Boyta har Schüco ASE 80.HI med 3-glas och behöver projekteras tillsammans med resten av konstruktionen för avsedd bostadsanvändning.",
      },
      {
        question: "Vilken grund passar bäst för Bostandard?",
        answer:
          "Det beror på tomtens förutsättningar. Vi projekterar den bästa lösningen – antingen isolerad platta på mark eller plintgrund med isolerat bjälklag.",
      },
    ],
    seoTitle:
      "Bostandard-paketet | Vinterbonad helårsstandard | Nordiska Inglasningar",
    seoDescription:
      "Bostandard-paketet med Schüco ASE 60, termiskt brutna profiler och 2-glas isolerglas 27 mm. Isolerat uterumstak med kanalplast 50 mm. Helårsbruk med vinterbonad standard.",
    seoKeywords:
      "bostandard uterum, vinterbonat uterum, Schüco ASE 60, helårsuterum, isolerat uterum, vinterträdgård",
    relatedGuides: [
      {
        title: "Vinterbonat uterum",
        href: "/uterum/vinterbonat/",
        description: "Allt om vinterbonade uterum och isolering",
      },
      {
        title: "Uterum pris",
        href: "/uterum/vara-paket/",
        description: "Komplett prisguide för alla paket",
      },
      {
        title: "Bygglov uterum",
        href: "/uterum/bygglov/",
        description: "Regler och ansökan",
      },
    ],
    nextPackage: {
      name: "Boyta",
      slug: "boyta-paket",
      desc: "Planeras för bostadsbruk med hög isoleringsnivå",
    },
    prevPackage: {
      name: "Vinter",
      slug: "vinterbonat-paket",
      desc: "Brutna köldbryggor, kanalplast 32 mm",
    },
  },
  "boyta-paket": {
    id: "boyta",
    slug: "boyta-paket",
    name: "Boyta",
    fullName: "Boyta-paketet",
    tagline: "Planeras för bostadsbruk",
    heroSubtitle:
      "Vårt mest avancerade paket. Schüco ASE 80.HI med 3-glas isolerglas, regelstomme med mineralull och högisolerat villatak är avsett för projekt som planeras för bostadsbruk.",
    season: "Januari - December",
    months: "12 månader",
    icon: Home,
    color: "text-navy",
    bgColor: "bg-navy/5",
    borderColor: "border-navy/20",
    heroImage: IMAGES.villa5,
    heroImageAlt:
      "Boyta-uterum med Schüco ASE 80.HI och projekterad helårsanvändning",
    galleryImages: [
      { src: IMAGES.villa5, alt: "Boyta-uterum med Schüco glaspartier" },
      {
        src: IMAGES.projekt33,
        alt: "Modernt boyta-uterum med stora glaspartier",
      },
      { src: IMAGES.projekt33_1, alt: "Interiör boyta-uterum med möblering" },
      { src: IMAGES.projekt40_1, alt: "Boyta-uterum med Schüco glaspartier" },
      { src: IMAGES.projekt30, alt: "Boyta-uterum sett utifrån" },
      { src: IMAGES.projekt30_1, alt: "Detalj av glasparti och anslutning" },
    ],
    glasSystem: "Schüco ASE 80.HI",
    glasTyp:
      "3-glas isolerglas 38 mm, argon + energibeläggning, laminerat ytter (U-värde parti ner mot 0,9 W/m²K)",
    tak: "Högisolerat villatak (minst 300 mm)",
    stomme: "Regelstomme 45x195 mm, Mineralull 195 mm",
    grund: "Isolerad platta på mark eller plintgrund",
    golv: "Isolerat golv med golvvärme (el/vatten/kombinerad)",
    el: "Komplett elinstallation med jordfelsbrytare",
    varme: "Golvvärme ingår (el/vatten/kombinerad)",
    bygglov: "Ingår (komplett bygglovshantering)",
    prisRange: "Från ca 500 000 kr",
    prisExempel: [
      { storlek: "15 kvm", pris: "ca 500 000 kr" },
      { storlek: "20 kvm", pris: "ca 650 000 kr" },
      { storlek: "30 kvm", pris: "ca 850 000 kr" },
    ],
    techSpecs: [
      { label: "Glassystem", value: "Schüco ASE 80.HI" },
      { label: "Glastyp", value: "3-glas isolerglas (Ug 0,6-0,8)" },
      { label: "U-värde (parti)", value: "Ner mot 0,9 W/m²K" },
      { label: "Profiler", value: "Termiskt bruten aluminium" },
      { label: "Tak", value: "Högisolerat villatak, minst 300 mm" },
      { label: "Stomme", value: "Regelstomme 45x195 mm, Mineralull 195 mm" },
      { label: "Vägg U-värde", value: "ca 0,20 W/m²K" },
      { label: "Grund", value: "Isolerad platta/plintgrund med radonspärr" },
      { label: "Golv", value: "Isolerat med golvvärme" },
      { label: "Bygglov", value: "Komplett bygglovshantering ingår" },
    ],
    varforValjaPunkter: [
      {
        title: "Räknas som bostadsyta",
        desc: "Planeras för bostadsbruk. Byggkrav och klassificering av boarea bedöms för den färdiga lösningen.",
      },
      {
        title: "Schüco ASE 80.HI",
        desc: "Världsledande lyft-skjutsystem med 3-glas isolerglas och U-värde ner mot 0,9.",
      },
      {
        title: "Samma standard som huset",
        desc: "Regelstomme med mineralull, högisolerat villatak och isolerad grund. Ingen kompromiss.",
      },
      {
        title: "Värdeökning",
        desc: "Ett extra rum kan påverka fastighetens värde. Någon viss värdeökning kan inte garanteras; be en lokal värderare bedöma din fastighet.",
      },
      {
        title: "Komplett dokumentation",
        desc: "Bygglov, konstruktionsritningar, energiberäkning, kontrollplan och slutbesiktning ingår.",
      },
      {
        title: "Opartisk konstruktör",
        desc: "En oberoende konstruktör granskar och godkänner alla tekniska lösningar.",
      },
    ],
    description:
      "Det mest avancerade paketet. Schüco ASE 80.HI med 3-glas isolerglas är avsett att projekteras för bostadsbruk.",
    detailedDescription:
      "Boyta-paketet är Nordiska Inglasningars flaggskepp för projekt som ska utformas för bostadsbruk. Med Schüco ASE 80.HI lyft-skjutsystem, 3-glas isolerglas och termiskt brutna aluminiumprofiler får du glaspartier med U-värde ner mot 0,9 W/m²K. Regelstommen i 45x195 mm med 195 mm mineralull ger ett vägg-U-värde på ca 0,20 W/m²K, och det högisolerade villataket med minst 300 mm isolering skapar ett komplett klimatskal. Grunden kan vara platta på mark med radonspärr eller bjälklag på plint med isolering över 270 mm. Allt byggs med samma standard som resten av huset, och klassificering som boarea (BOA) behöver bedömas för den färdiga lösningen.",
    idealFor: [
      "Dig som vill öka bostadens värde med extra boarea",
      "Familjer som behöver ett extra rum med full komfort",
      "Den som vill ha det bästa som finns, utan kompromisser",
      "Husägare som planerar att sälja och vill maximera värdet",
    ],
    faq: [
      {
        question: "Vad innebär det att uterummet räknas som boyta?",
        answer:
          "Boyta är vårt paketnamn för en lösning avsedd att projekteras för bostadsbruk. Om den färdiga ytan är boarea eller biarea avgörs av mätregler och utförande. Ett paketnamn eller visst glasparti ger inte automatiskt godkännande.",
      },
      {
        question: "Hur mycket ökar fastighetsvärdet?",
        answer:
          "Värdet beror på fastigheten, läget, kvaliteten och efterfrågan. Paketet innebär inte ett löfte om viss värdeökning. Klassificering av boarea och biarea behöver bedömas separat.",
      },
      {
        question: "Vad är Schüco ASE 80.HI?",
        answer:
          "Schüco ASE 80.HI är ett högpresterande lyft-skjutsystem med termiskt brutna aluminiumprofiler och 3-glas isolerglas. U-värdet för hela partiet ligger ner mot 0,9 W/m²K. Schüco är världsledande inom fasadsystem.",
      },
      {
        question: "Ingår bygglov och konstruktör?",
        answer:
          "Ja, komplett bygglovshantering ingår: bygglovsritningar, konstruktionsritningar av opartisk konstruktör, energiberäkning vid behov, kontrollplan och slutbesiktning.",
      },
      {
        question: "Vad kostar Boyta-paketet med ROT-avdrag?",
        answer:
          "Se paketets riktpris före eventuellt ROT. Slutpris beror på mått, tillval och platsens förutsättningar. ROT kan endast beräknas på berättigad arbetskostnad när villkoren är uppfyllda.",
      },
    ],
    seoTitle: "Boyta-uterum med Schüco 3-glas | Från 500 000 kr",
    seoDescription:
      "Boyta-paketet är avsett att projekteras för bostadsbruk. Schüco ASE 80.HI, 3-glas, regelstomme och högisolerat tak. Från ca 500 000 kr. Ta kontakt.",
    seoKeywords:
      "boyta uterum, uterum bostadsyta, BBR uterum, Schüco ASE 80, uterum helår, uterum 500000, uterum värdeökning",
    relatedGuides: [
      {
        title: "Boyta: Komplett guide",
        href: "/uterum/boyta/",
        description: "Läs om boarea, tekniska krav och projektering.",
      },
      {
        title: "Vår fabrik i Eskilstuna",
        href: "/uterum/var-fabrik/",
        description:
          "Så tillverkas ditt uterum i vår egen fabrik med Schüco-system.",
      },
      {
        title: "Vi löser det andra inte kan",
        href: "/uterum/fragor/vi-loser-det-andra-inte-kan/",
        description: "Skräddarsydda lösningar för komplexa projekt.",
      },
    ],
    prevPackage: {
      name: "Vinterbonat",
      slug: "vinterbonat-paket",
      desc: "Schüco ASE 60 för helårsbruk med projektspecifik kravbedömning.",
    },
  },
};
